import * as React from "react";
import { Input, Spin } from "antd";
import { SearchOutlined, CloseOutlined } from "@ant-design/icons";
import { BrowserColumnProps, BrowserLink, BrowserData } from "../types";
import { BrowserColumnItem } from "./BrowserColumnItem";
import { BrowserDataItem } from "./BrowserDataItem";
import styles from "../BrowserServiceModal.module.scss";

export const BrowserColumn = React.forwardRef<HTMLDivElement, BrowserColumnProps>(
  (
    {
      type,
      title,
      items,
      isLoading,
      isActive,
      selectedHref,
      filter,
      focusedIndex,
      selectionType,
      selections,
      isClickable = true,
      canNavigateDeeper = true,
      onItemClick,
      onItemSelect,
      onFilterChange,
      onFocusChange,
      onColumnFocus,
    },
    ref
  ) => {
    const searchInputRef = React.useRef<HTMLInputElement>(null);
    const listRef = React.useRef<HTMLDivElement>(null);

    // Filter items based on search
    const filteredItems = React.useMemo(() => {
      if (!items) return [];
      if (!filter) return items;

      const lowerFilter = filter.toLowerCase();
      return items.filter((item) => {
        const label = "_cpln" in item ? item._cpln.label : item.label || "";
        return label.toLowerCase().includes(lowerFilter);
      });
    }, [items, filter]);

    const totalCount = items?.length || 0;
    const filteredCount = filteredItems.length;
    const showCount = filter ? `${filteredCount}/${totalCount}` : String(totalCount);

    // Check if an item is a data item
    const isDataItem = (item: BrowserLink | BrowserData): item is BrowserData => {
      return item._type === "data";
    };

    // Handle search input change with debounce
    const handleSearchChange = React.useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        onFilterChange(e.target.value);
      },
      [onFilterChange]
    );

    const handleClearSearch = React.useCallback(() => {
      onFilterChange("");
      searchInputRef.current?.focus();
    }, [onFilterChange]);

    // Expose search input ref for keyboard navigation
    React.useImperativeHandle(
      ref,
      () => {
        const div = listRef.current as HTMLDivElement;
        if (div) {
          (div as any).searchInputRef = searchInputRef;
        }
        return div as HTMLDivElement;
      },
      []
    );

    return (
      <div
        className={`${styles.column} ${isActive ? styles.columnActive : ""}`}
        tabIndex={0}
        ref={listRef}
        onFocus={onColumnFocus}
        role="region"
        aria-label={title}
      >
        <div className={styles.columnHeader}>
          <span className={styles.columnHeaderTitle}>{title}</span>
          <span className={styles.columnHeaderCount}>({showCount})</span>
        </div>

        <div className={styles.columnSearch}>
          <Input
            ref={searchInputRef as any}
            placeholder={`Search ${title.toLowerCase()}...`}
            value={filter}
            onChange={handleSearchChange}
            prefix={<SearchOutlined className={styles.searchIcon} />}
            suffix={
              filter ? (
                <button
                  type="button"
                  className={styles.searchClear}
                  onClick={handleClearSearch}
                  aria-label="Clear search"
                >
                  <CloseOutlined style={{ fontSize: 12 }} />
                </button>
              ) : null
            }
            size="small"
            aria-label={`Search ${title}`}
          />
        </div>

        <div
          className={styles.columnList}
          role="listbox"
          aria-label={`${title} options`}
        >
          {isLoading ? (
            <div className={styles.columnLoading}>
              <Spin size="small" />
            </div>
          ) : filteredItems.length === 0 ? (
            <div className={styles.columnEmpty}>
              {filter
                ? `No ${title.toLowerCase()} match your search`
                : `No ${title.toLowerCase()} found in your cloud account`}
            </div>
          ) : (
            filteredItems.map((item, index) => {
              if (isDataItem(item)) {
                const isSelected = selections.some((s) => s._cpln.ref === item._cpln.ref);
                const isItemActive =
                  isSelected ||
                  (!!item._cpln.hasChildren && selectedHref === item._cpln.href);

                return (
                  <BrowserDataItem
                    key={item._cpln.href || item._cpln.ref}
                    item={item}
                    isActive={isItemActive}
                    isSelected={isSelected}
                    isFocused={focusedIndex === index}
                    selectionType={selectionType}
                    canBeSelectedAsParent={isClickable}
                    canNavigateDeeper={canNavigateDeeper}
                    onClick={() => onItemClick(item)}
                    onSelect={() => onItemSelect?.(item)}
                  />
                );
              }

              const linkItem = item as BrowserLink;
              return (
                <BrowserColumnItem
                  key={linkItem.href}
                  item={linkItem}
                  isActive={selectedHref === linkItem.href}
                  isClickable={isClickable}
                  isFocused={focusedIndex === index}
                  onClick={() => onItemClick(linkItem)}
                />
              );
            })
          )}
        </div>
      </div>
    );
  }
);

BrowserColumn.displayName = "BrowserColumn";
